import * as React from 'react';
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';

import { ChevronRightIcon } from '../../icons';
import { cx } from '../../utils/cx';
import CloseButton from '../Button/CloseButton';

import type {
  DialogProps as AriaDialogProps,
  ModalOverlayProps as AriaModalOverlayProps,
  ModalRenderProps as AriaModalRenderProps,
} from 'react-aria-components';

interface IModalOverlayProps extends AriaModalOverlayProps, React.RefAttributes<HTMLDivElement> {}

export const ModalOverlay = (props: IModalOverlayProps) => {
  return (
    <AriaModalOverlay
      {...props}
      className={(state) =>
        cx(
          'bg-overlay/70 outline-hidden fixed inset-0 z-50 flex min-h-dvh w-full items-center justify-end pl-6 ease-linear md:pl-10',
          state.isEntering && 'animate-in fade-in duration-300',
          state.isExiting && 'animate-out fade-out duration-500',
          typeof props.className === 'function' ? props.className(state) : props.className
        )
      }
    />
  );
};
ModalOverlay.displayName = 'ModalOverlay';

interface IModalProps extends AriaModalOverlayProps, React.RefAttributes<HTMLDivElement> {}

export const Modal = (props: IModalProps) => (
  <AriaModal
    {...props}
    className={(state) =>
      cx(
        'max-w-130 inset-y-0 right-0 m-3 h-[calc(100%-24px)] w-[calc(100%-12px)] shadow-xl transition',
        state.isEntering && 'animate-in slide-in-from-right duration-300',
        state.isExiting && 'animate-out slide-out-to-right duration-500',
        typeof props.className === 'function' ? props.className(state) : props.className
      )
    }
  />
);
Modal.displayName = 'Modal';

interface IDialogProps extends AriaDialogProps, React.RefAttributes<HTMLElement> {}

export const Dialog = (props: IDialogProps) => (
  <AriaDialog
    role="dialog"
    {...props}
    className={cx(
      'bg-primary ring-secondary_alt outline-hidden relative flex size-full flex-col items-start overflow-y-auto overscroll-none ring-1',
      props.className
    )}
  />
);
Dialog.displayName = 'Dialog';

interface ISlideoutMenuProps extends Omit<AriaModalOverlayProps, 'children'>, React.RefAttributes<HTMLDivElement> {
  children: React.ReactNode | ((children: AriaModalRenderProps & { close: () => void }) => React.ReactNode);
  dialogClassName?: string;
  ariaLabel?: string;
}

const Menu = ({ children, dialogClassName, ariaLabel, ...props }: ISlideoutMenuProps) => {
  return (
    <ModalOverlay {...props} isDismissable={props.isDismissable ?? true}>
      <Modal
        className={(state) => cx(typeof props.className === 'function' ? props.className(state) : props.className)}
      >
        {(state) => (
          <Dialog aria-label={ariaLabel} className={cx('bg-secondary rounded-2xl', dialogClassName)}>
            {({ close }) => {
              return typeof children === 'function' ? children({ ...state, close }) : children;
            }}
          </Dialog>
        )}
      </Modal>
    </ModalOverlay>
  );
};
Menu.displayName = 'SlideoutMenu';

const Content = ({ role = 'main', ...props }: React.ComponentPropsWithRef<'div'>) => {
  return (
    <div
      role={role}
      {...props}
      className={cx('z-1 mt-[-22px] flex w-full flex-col gap-6 px-4 pb-5 pt-0 md:px-5', props.className)}
    />
  );
};
Content.displayName = 'SlideoutContent';

interface ISlideoutHeaderProps extends React.ComponentPropsWithRef<'header'> {
  leftContent?: React.ReactNode;
  onClose?: () => void;
}

const Header = ({ className, children, leftContent, onClose, ...props }: ISlideoutHeaderProps) => {
  return (
    <>
      <div className="bg-brand-500 sticky top-0 z-20 flex w-full items-center justify-between pb-1 pl-5 pr-2 pt-4">
        {leftContent ?? <div />}
        <CloseButton size="md" onClick={onClose} className="hover:bg-brand-600 text-white hover:text-white" />
      </div>
      <header {...props} className={cx('z-1 bg-brand-500 pb-11.5 flex w-full flex-col items-center', className)}>
        <div className="flex w-full flex-col items-center pt-3">{children}</div>
      </header>
    </>
  );
};
Header.displayName = 'SlideoutHeader';

const Footer = (props: React.ComponentPropsWithRef<'footer'>) => {
  return <footer {...props} className={cx('w-full p-4 md:px-5', props.className)} />;
};
Footer.displayName = 'SlideoutFooter';

const Container = (props: React.ComponentPropsWithRef<'div'>) => {
  return (
    <div
      {...props}
      className={cx(
        'bg-primary border-tertiary divide-tertiary shadow-xs flex flex-col divide-y overflow-hidden rounded-2xl border',
        props.className
      )}
    />
  );
};
Container.displayName = 'SlideoutContainer';

interface IRowProps extends React.PropsWithChildren {
  className?: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  onClick?: () => void;
}

const Row = ({ className, icon, title, onClick, children }: IRowProps) => {
  return (
    <div
      onClick={onClick}
      className={cx(
        'h-18 flex max-w-full items-center gap-2 p-4',
        !!onClick && 'hover:bg-utility-brand-25 cursor-pointer',
        className
      )}
    >
      <div className="flex max-w-full flex-1 items-center">
        <div className="text-md text-tertiary mr-4 flex flex-1 items-center gap-1 font-medium">
          {icon}
          {title}
        </div>
        {children}
        {onClick && <ChevronRightIcon className="text-quaternary size-4.5 ml-2 shrink-0" />}
      </div>
    </div>
  );
};
Row.displayName = 'SlideoutRow';

const SlideoutMenu = Menu as typeof Menu & {
  Trigger: typeof AriaDialogTrigger;
  Content: typeof Content;
  Header: typeof Header;
  Footer: typeof Footer;
  Container: typeof Container;
  Row: typeof Row;
};
SlideoutMenu.displayName = 'SlideoutMenu';

SlideoutMenu.Trigger = AriaDialogTrigger;
SlideoutMenu.Content = Content;
SlideoutMenu.Header = Header;
SlideoutMenu.Footer = Footer;
SlideoutMenu.Container = Container;
SlideoutMenu.Row = Row;

export default SlideoutMenu;
